import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart3, Globe, Eye, Users, Trophy, ExternalLink, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnalyticsPage() {
  const { organization, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (organization?.org_id) fetchAnalytics();
  }, [organization]);

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/org-analytics/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('Analytics fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const maxVisits = data?.daily_visits?.length > 0 
    ? Math.max(...data.daily_visits.map(d => d[1])) 
    : 1;

  return (
    <DashboardLayout>
      <div data-testid="analytics-page" className="space-y-8">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
            Analytics
          </h1>
          <p className="text-gray-600 mt-2">Visitor traffic and engagement for your tournament</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4 text-center">
              <Eye className="mx-auto text-emerald-500 mb-2" size={24} />
              <p className="text-2xl font-bold text-black">{data?.total_visits || 0}</p>
              <p className="text-gray-500 text-xs uppercase">Page Views</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4 text-center">
              <Users className="mx-auto text-blue-500 mb-2" size={24} />
              <p className="text-2xl font-bold text-black">{data?.unique_visitors || 0}</p>
              <p className="text-gray-500 text-xs uppercase">Unique Visitors</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4 text-center">
              <Trophy className="mx-auto text-yellow-500 mb-2" size={24} />
              <p className="text-2xl font-bold text-black">{data?.match_stats?.completed || 0}/{data?.match_stats?.total || 0}</p>
              <p className="text-gray-500 text-xs uppercase">Matches Done</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4 text-center">
              <TrendingUp className="mx-auto text-orange-500 mb-2" size={24} />
              <p className="text-2xl font-bold text-black">{data?.subscriber_count || 0}</p>
              <p className="text-gray-500 text-xs uppercase">Subscribers</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Visits Chart */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200 pb-3">
              <CardTitle className="text-black font-heading uppercase text-sm flex items-center gap-2">
                <BarChart3 size={16} className="text-emerald-500" /> Daily Visits (30 days)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {data?.daily_visits?.length > 0 ? (
                <div className="flex items-end gap-1 h-32">
                  {data.daily_visits.slice(-30).map(([date, count], i) => (
                    <div
                      key={date}
                      className="flex-1 bg-emerald-500/60 hover:bg-emerald-500 rounded-t transition-colors cursor-pointer group relative"
                      style={{ height: `${Math.max((count / maxVisits) * 100, 4)}%` }}
                      title={`${date}: ${count} visits`}
                    >
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-black/90 text-black text-xs px-2 py-1 rounded whitespace-nowrap">
                        {date.slice(5)}: {count}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">No visit data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Top Pages */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200 pb-3">
              <CardTitle className="text-black font-heading uppercase text-sm flex items-center gap-2">
                <Eye size={16} className="text-blue-500" /> Most Visited Pages
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {data?.page_views?.length > 0 ? (
                <div className="space-y-2">
                  {data.page_views.slice(0, 8).map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-gray-700 text-sm truncate flex-1 mr-4">{p.page}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(p.views / (data.page_views[0]?.views || 1)) * 100}%` }} />
                        </div>
                        <span className="text-black text-sm font-mono w-8 text-right">{p.views}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">No page view data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Geography */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200 pb-3">
              <CardTitle className="text-black font-heading uppercase text-sm flex items-center gap-2">
                <Globe size={16} className="text-purple-500" /> Visitor Locations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {data?.geographic?.length > 0 ? (
                <div className="space-y-2">
                  {data.geographic.map((g, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-gray-700 text-sm">{g.country}</span>
                      <span className="text-black text-sm font-mono">{g.visits}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">No geographic data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Traffic Sources */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200 pb-3">
              <CardTitle className="text-black font-heading uppercase text-sm flex items-center gap-2">
                <ExternalLink size={16} className="text-orange-500" /> Traffic Sources
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {data?.referrers?.length > 0 ? (
                <div className="space-y-2">
                  {data.referrers.map((r, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-gray-700 text-sm truncate flex-1 mr-4">{r.source}</span>
                      <span className="text-black text-sm font-mono">{r.visits}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">No referrer data yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
